import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Cookie, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  TRACKING_WINDOW_EVENTS,
  TRACKING_STORAGE_KEYS,
  CONSENT_TTL_DAYS,
  CONSENT_VERSION,
} from "@/config/tracking";
import { emitConsentSnapshot } from "@/lib/consentEmitter";

interface CookiePreferences {
  functional: boolean;
  analytical: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = TRACKING_STORAGE_KEYS.CONSENT;
const COOKIE_PREFERENCES_KEY = TRACKING_STORAGE_KEYS.PREFERENCES;
const COOKIE_CONSENT_TIMESTAMP_KEY = "cookie-consent-timestamp";
const COOKIE_CONSENT_VERSION_KEY = "cookie-consent-version";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

const isConsentExpired = (): boolean => {
  try {
    const ts = localStorage.getItem(COOKIE_CONSENT_TIMESTAMP_KEY);
    const ver = localStorage.getItem(COOKIE_CONSENT_VERSION_KEY);
    if (!ts || ver !== CONSENT_VERSION) return true;
    const ageMs = Date.now() - parseInt(ts, 10);
    return ageMs > CONSENT_TTL_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return true;
  }
};

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    functional: true, // Always required
    analytical: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent || isConsentExpired()) {
      // Render after first paint to avoid blocking the initial render.
      const raf = requestAnimationFrame(() => setShowBanner(true));
      return () => cancelAnimationFrame(raf);
    }
  }, []);

  // Listen for footer "Cookie-instellingen" link → re-open banner with details visible.
  useEffect(() => {
    const handleReopen = () => {
      try {
        const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          setPreferences({
            functional: true,
            analytical: !!parsed.analytical,
            marketing: !!parsed.marketing,
          });
        }
      } catch {}
      setShowDetails(true);
      setShowBanner(true);
    };
    window.addEventListener(TRACKING_WINDOW_EVENTS.COOKIE_BANNER_REOPEN, handleReopen);
    return () => window.removeEventListener(TRACKING_WINDOW_EVENTS.COOKIE_BANNER_REOPEN, handleReopen);
  }, []);

  const savePreferences = async (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem(COOKIE_CONSENT_TIMESTAMP_KEY, Date.now().toString());
    localStorage.setItem(COOKIE_CONSENT_VERSION_KEY, CONSENT_VERSION);
    setShowBanner(false);

    // Emit gtag consent update + cookie_consent_update GTM event.
    await emitConsentSnapshot({ prefs, includeDefault: false });

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(TRACKING_WINDOW_EVENTS.COOKIE_CONSENT_UPDATED, { detail: prefs })
      );
    }
  };

  const acceptAll = () => {
    const allAccepted = { functional: true, analytical: true, marketing: true };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
  };

  const acceptSelected = () => {
    savePreferences(preferences);
  };

  const rejectAll = () => {
    const onlyFunctional = { functional: true, analytical: false, marketing: false };
    setPreferences(onlyFunctional);
    savePreferences(onlyFunctional);
  };

  const openDetails = () => setShowDetails(true);
  const closeDetails = () => setShowDetails(false);

  if (!showBanner) return null;

  const checkboxBlock = (
    <div className="space-y-3">
      <div className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
        <Checkbox id="functional" checked disabled />
        <div className="space-y-1">
          <Label htmlFor="functional" className="font-medium cursor-pointer">
            Functionele cookies (verplicht)
          </Label>
          <p className="text-xs text-muted-foreground">
            Nodig voor de basisfunctionaliteit van de website, zoals inloggen en navigatie.
          </p>
        </div>
      </div>

      <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
        <Checkbox
          id="analytical"
          checked={preferences.analytical}
          onCheckedChange={(checked) =>
            setPreferences(prev => ({ ...prev, analytical: !!checked }))
          }
        />
        <div className="space-y-1">
          <Label htmlFor="analytical" className="font-medium cursor-pointer">
            Analytische cookies
          </Label>
          <p className="text-xs text-muted-foreground">
            Helpen ons begrijpen hoe bezoekers de website gebruiken via Google Analytics.
            Geen data buiten EU/EEA.
          </p>
        </div>
      </div>

      <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
        <Checkbox
          id="marketing"
          checked={preferences.marketing}
          onCheckedChange={(checked) =>
            setPreferences(prev => ({ ...prev, marketing: !!checked }))
          }
        />
        <div className="space-y-1">
          <Label htmlFor="marketing" className="font-medium cursor-pointer">
            Marketing cookies
          </Label>
          <p className="text-xs text-muted-foreground">
            Voor gepersonaliseerde advertenties via Meta Pixel en Leadinfo websitebezoek-tracking.
          </p>
        </div>
      </div>
    </div>
  );

  const privacyLine = (
    <p className="text-xs text-muted-foreground">
      Meer informatie vind je in ons{" "}
      <a href="/privacy" className="underline hover:text-primary">
        privacybeleid
      </a>
      . Vragen? Mail naar{" "}
      <a href="mailto:compliance@deerpeter.nl" className="underline hover:text-primary">
        compliance@deerpeter.nl
      </a>
    </p>
  );

  return (
    <>
      {/* Mobile: compact bottom sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[100] md:hidden bg-background border-t shadow-[0_-8px_30px_rgba(0,0,0,0.12)] p-4 pb-6 animate-in slide-in-from-bottom-4 duration-300"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title-mobile"
      >
        {!showDetails ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-primary" />
                <h2 id="cookie-consent-title-mobile" className="font-semibold text-base">
                  Cookie-instellingen
                </h2>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">
                Wij gebruiken cookies voor een betere ervaring. Kies hieronder je voorkeuren.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                className="w-full min-h-[44px] h-auto py-2.5"
                onClick={acceptAll}
              >
                Alles accepteren
              </Button>
              <Button
                variant="outline"
                className="w-full min-h-[44px] h-auto py-2.5"
                onClick={rejectAll}
              >
                Alleen noodzakelijk
              </Button>
              <Button
                variant="link"
                className="w-full min-h-[44px] h-auto py-2.5"
                onClick={openDetails}
              >
                Instellingen
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={closeDetails}>
                Terug
              </Button>
              <h2 className="font-semibold text-base">Cookie-instellingen</h2>
            </div>

            {checkboxBlock}
            {privacyLine}

            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                className="w-full min-h-[44px] h-auto py-2.5"
                onClick={rejectAll}
              >
                Alleen noodzakelijk
              </Button>
              <Button
                className="w-full min-h-[44px] h-auto py-2.5"
                onClick={acceptSelected}
              >
                Selectie opslaan
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop: centered modal */}
      <div
        className="fixed inset-0 z-[100] hidden md:flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-in fade-in duration-300"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
      >
        <Card className={cn(
          "w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl border-2 pointer-events-auto",
          "animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
        )}>
          <div className="w-full px-2 md:px-4 py-2">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-primary" />
                <CardTitle id="cookie-consent-title" className="text-lg">Cookie-instellingen</CardTitle>
              </div>
              <CardDescription>
                Wij gebruiken cookies om je ervaring te verbeteren. Maak een keuze om verder te gaan — zonder keuze
                werken bepaalde functies van de site niet. Je keuze kun je later wijzigen via 'Cookie-instellingen'
                onderin elke pagina.
              </CardDescription>
            </CardHeader>

            {showDetails && (
              <CardContent className="pt-0 pb-3 space-y-4">
                {checkboxBlock}
                {privacyLine}
              </CardContent>
            )}

            <CardFooter className="flex flex-wrap items-center gap-2 pt-0">
              {!showDetails ? (
                <>
                  <Button variant="ghost" size="sm" onClick={openDetails}>
                    <Settings className="w-4 h-4 mr-2" />
                    Instellingen
                  </Button>
                  <div className="flex flex-wrap gap-2 ml-auto">
                    <Button variant="outline" size="sm" onClick={rejectAll}>
                      Alleen noodzakelijk
                    </Button>
                    <Button variant="outline" size="sm" onClick={acceptAll}>
                      Alles accepteren
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Button variant="ghost" size="sm" onClick={closeDetails}>
                    Terug
                  </Button>
                  <div className="flex flex-wrap gap-2 ml-auto">
                    <Button variant="outline" size="sm" onClick={rejectAll}>
                      Alleen noodzakelijk
                    </Button>
                    <Button variant="outline" size="sm" onClick={acceptSelected}>
                      Selectie opslaan
                    </Button>
                  </div>
                </>
              )}
            </CardFooter>
          </div>
        </Card>
      </div>
    </>
  );
};

